// Central token/session storage.
//
// Persistence: localStorage (NOT sessionStorage) so a logged-in session survives page reloads,
// new tabs, and browser restarts. sessionStorage is scoped to a single tab and is cleared when the
// tab closes, which is why opening the base URL in a new tab used to fall through to the login page.
// const store = window.localStorage;

// A stored value is only "usable" if it's a non-empty string that isn't one of the
// stringified-nullish sentinels localStorage ends up with when undefined/null is written
// via setItem (e.g. `setAccessToken(undefined)` persists the literal string "undefined").
// Shared so the login entry paths and the API layers agree on what counts as a real
// token/id, instead of relying on plain truthiness ("undefined" is a truthy string).
export const isValidStoredValue = (value) =>
  value !== null &&
  value !== undefined &&
  value !== "" &&
  value !== "null" &&
  value !== "undefined";

// Setters and Getters for Access Token
export function setAccessToken(aToken) {
  window.localStorage.setItem("accessToken", aToken);
}
export function getAccessToken() {
  return window.localStorage.getItem("accessToken");
}
export function clearAccessToken() {
  window.localStorage.removeItem("accessToken");
}

// Setters and Getters for Refresh Token
export function setRefreshToken(rToken) {
  window.localStorage.setItem("refreshToken", rToken);
}
export function getRefreshToken() {
  return window.localStorage.getItem("refreshToken");
}
export function clearRefreshToken() {
  window.localStorage.removeItem("refreshToken");
}

// Setters and Getters for guId
export function setGuId(guid) {
  window.localStorage.setItem("guid", guid);
  window.localStorage.setItem("guId", guid);
}
export function getGuId() {
  return window.localStorage.getItem("guid") || window.localStorage.getItem("guId");
}
export function clearGuId() {
  window.localStorage.removeItem("guid");
  window.localStorage.removeItem("guId");
}

// Setters and Getters for loginid
export function setLoginId(loginId) {
  window.localStorage.setItem("loginid", loginId);
  window.localStorage.setItem("id", loginId);
}
export function getLoginId() {
  return window.localStorage.getItem("loginid") || window.localStorage.getItem("id");
}
export function clearLoginId() {
  window.localStorage.removeItem("loginid");
  window.localStorage.removeItem("id");
}

// Setters and Getters for First Name
export function setFirstname(firstname) {
  window.localStorage.setItem("firstname", firstname);
}
export function getFirstname() {
  return window.localStorage.getItem("firstname");
}
export function clearFirstname() {
  window.localStorage.removeItem("firstname");
}

// Setters and Getters for Last Name
export function setLastname(lastname) {
  window.localStorage.setItem("lastname", lastname);
}
export function getLastname() {
  return window.localStorage.getItem("lastname");
}
export function clearLastname() {
  window.localStorage.removeItem("lastname");
}
// Setters and Getters for Email
export function setEmailID(email) {
  window.localStorage.setItem("email", email);
}
export function getEmailID() {
  return window.localStorage.getItem("email");
}
export function clearEmailID() {
  window.localStorage.removeItem("email");
}

// Setters and Getters for MakeInvitations
export function setMakeInvitations(makeInvitations) {
  window.localStorage.setItem("MakeInvitations", String(makeInvitations));
}
export function getMakeInvitations() {
  return window.localStorage.getItem("MakeInvitations");
}
export function clearMakeInvitations() {
  window.localStorage.removeItem("MakeInvitations");
}

// Setters and Getters for Language
export function setLanguage(language) {
  window.localStorage.setItem("language", language);
}
export function getLanguage() {
  return window.localStorage.getItem("language");
}
export function clearLanguage() {
  window.localStorage.removeItem("language");
}

const SIDEBAR_COLLAPSED_KEY = "sidebarCollapsed";
const SSO_LOGIN_KEY = "isSSOLogin";
const HAS_EMERGENCY_ROLES_KEY = "HasEmergencyRoles";
const DEFAULT_HAS_EMERGENCY_ROLES = 1;

export function setSidebarCollapsed(isCollapsed) {
  window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(Boolean(isCollapsed)));
}

export function getSidebarCollapsed() {
  return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true";
}

export function clearSidebarCollapsed() {
  window.localStorage.removeItem(SIDEBAR_COLLAPSED_KEY);
}

// Setters and Getters for SSO login state
export function setSSOLogin(isSSOLogin) {
  window.localStorage.setItem(SSO_LOGIN_KEY, String(Number(Boolean(isSSOLogin))));
}

export function getSSOLogin() {
  return window.localStorage.getItem(SSO_LOGIN_KEY) === "1";
}

export function clearSSOLogin() {
  window.localStorage.removeItem(SSO_LOGIN_KEY);
}

// Setters and Getters for emergency roles visibility
export function setHasEmergencyRoles(hasEmergencyRoles) {
  const normalizedValue = String(Number(hasEmergencyRoles) === 1 ? 1 : 0);
  window.localStorage.setItem(HAS_EMERGENCY_ROLES_KEY, normalizedValue);
  window.dispatchEvent(
    new CustomEvent("hasEmergencyRolesChanged", { detail: Number(normalizedValue) })
  );
}

export function getHasEmergencyRoles() {
  const storedValue = window.localStorage.getItem(HAS_EMERGENCY_ROLES_KEY);
  if (storedValue === null) {
    return DEFAULT_HAS_EMERGENCY_ROLES;
  }
  return Number(storedValue) === 0 ? 0 : 1;
}

export function clearHasEmergencyRoles() {
  window.localStorage.removeItem(HAS_EMERGENCY_ROLES_KEY);
  window.dispatchEvent(
    new CustomEvent("hasEmergencyRolesChanged", { detail: 0 })
  );
}

export function syncHasEmergencyRolesFromPayload(payload, options = {}) {
  const { defaultToVisibleOnMissing = false } = options;
  const records = payload?.Data?.Records ?? payload?.data?.Records ?? payload?.data?.records;
  const hasEmergencyRoles = records?.HasEmergencyRoles;

  if (hasEmergencyRoles !== undefined && hasEmergencyRoles !== null) {
    setHasEmergencyRoles(hasEmergencyRoles);
    return Number(hasEmergencyRoles) === 0 ? 0 : 1;
  }

  if (defaultToVisibleOnMissing) {
    setHasEmergencyRoles(DEFAULT_HAS_EMERGENCY_ROLES);
    return DEFAULT_HAS_EMERGENCY_ROLES;
  }

  return getHasEmergencyRoles();
}


// Persist a full auth session in one place (shared by the login paths that receive a complete
// user record: manual login + Keycloak). Callers pass already-resolved values — each path
// resolves field names / defaults from its own response shape — and every field is written
// exactly as passed. `makeInvitations` is only written when provided (some paths omit it).
export function persistSession({
  isSSOLogin,
  accessToken,
  refreshToken,
  guid,
  loginId,
  firstName,
  lastName,
  email,
  makeInvitations,
  hasEmergencyRoles,
} = {}) {
  setSSOLogin(isSSOLogin);
  setAccessToken(accessToken);
  setRefreshToken(refreshToken);
  setGuId(guid);
  setLoginId(loginId);
  setFirstname(firstName);
  setLastname(lastName);
  setEmailID(email);
  if (makeInvitations !== undefined && makeInvitations !== null) {
    setMakeInvitations(makeInvitations);
  }
  if (hasEmergencyRoles !== undefined && hasEmergencyRoles !== null) {
    setHasEmergencyRoles(hasEmergencyRoles);
  }
}

// Write the profile fields carried by a Profile_api Records object into the session window.localStorage.
// Shared by the login paths that hydrate profile from a follow-up Profile call (SSO query-param
// via LoginEvent, and AutoLogin). Each field is written only when present, matching prior
// behavior. Callers keep their own React `setUser(...)` (component state); LoginEvent also keeps
// its `setMakeInvitations(...)` (AutoLogin never wrote it).
export function hydrateProfileFromRecords(records = {}) {
  if (records?.FirstName) setFirstname(records.FirstName);
  if (records?.LastName) setLastname(records.LastName);
  if (records?.PrimaryEmail) setEmailID(records.PrimaryEmail);

  const loginId = records?.Id || records?.id;
  if (loginId) setLoginId(loginId);

  if (records?.Notifications !== undefined && records?.Notifications !== null) {
    window.localStorage.setItem("Notifications", records.Notifications);
  }

  if (records?.HasEmergencyRoles !== undefined && records?.HasEmergencyRoles !== null) {
    setHasEmergencyRoles(records.HasEmergencyRoles);
  }

  if (records?.HasEmergencyRoles !== undefined && records?.HasEmergencyRoles !== null) {
    setHasEmergencyRoles(records.HasEmergencyRoles);
  }
}

// Setters and Getters for impersonated
export function setImpersonated(impersonated) {
  window.localStorage.setItem("impersonated", impersonated);
}
export function getImpersonated() {
  return window.localStorage.getItem("impersonated");
}
export function clearImpersonated() {
  window.localStorage.removeItem("impersonated");
}
